import { isAdministratorRole, matchesAnyRole } from '../../../utils/instanceRoleInheritance';

export const findDecoratorDestination = (destinations, decoratorId) => (
  destinations.find((destination) => String(destination.id) === String(decoratorId)) || null
);

export const decoratorRuleAddressId = (decoratorId) => `decorator-rule-${decoratorId}`;

export const isDecoratorRuleAddress = (addressId, decoratorId) => (
  /^[1-9][0-9]*$/.test(String(decoratorId || '')) &&
  String(addressId) === decoratorRuleAddressId(decoratorId)
);

export const decoratorAddressLines = (address = {}) => [
  address.street || address.streetAddress,
  address.suite || address.suiteApt,
  [address.city, address.state || address.stateProvince].filter(Boolean).join(', '),
  address.postal || address.postalZipCode,
  address.country,
].filter(Boolean);

export const hasCompleteDecoratorReceivingAddress = (destination) => (
  Boolean(destination?.address) &&
  ['streetAddress', 'city', 'stateProvince', 'postalZipCode', 'country']
    .every((field) => String(destination.address[field] ?? '').trim().length > 0)
);

export const configuredDecoratorAddressOption = (destination) => {
  if (!destination?.rule || !hasCompleteDecoratorReceivingAddress(destination)) return null;
  const recipientName = destination.address.addressName || destination.address.companyName ||
    destination.recipientName || destination.rule.poRecipientName || destination.name;
  return {
    ...destination.address,
    id: decoratorRuleAddressId(destination.id),
    name: [recipientName, ...decoratorAddressLines(destination.address)].filter(Boolean).join(' · '),
    recipientName,
  };
};

export const initialDecoratorAddress = (destination, savedAddressId, { savedAddressComplete = true } = {}) => {
  // Preserve complete historical addresses (including administrator overrides).
  // An incomplete/dangling legacy ID cannot satisfy PO readiness; the current
  // configured default is the only safe repair for those saved routes.
  if (savedAddressComplete && savedAddressId != null &&
      String(savedAddressId).trim() !== '' && String(savedAddressId) !== '0') {
    return savedAddressId;
  }
  if (destination?.rule?.visibleInShipping === false) return '';
  return configuredDecoratorAddressOption(destination)?.id || '';
};

export const changeDecoratorDestination = (form, nextId, destinations) => {
  if (String(form.decorator || '') === String(nextId || '')) return form;
  return {
    ...form,
    decorator: nextId,
    decoratorAddress: initialDecoratorAddress(findDecoratorDestination(destinations, nextId), null),
  };
};

export const decoratorAddressLockedForRole = (destination, role) => (
  hasCompleteDecoratorReceivingAddress(destination) &&
  destination?.rule?.lockSalesAddress === true &&
  !isAdministratorRole(role) &&
  matchesAnyRole(role, ['order-rep', 'client-rep'])
);

export const decoratorShippingChoices = (destinations, currentId, savedId) => destinations.filter((destination) => (
  destination.rule?.visibleInShipping !== false ||
  (String(destination.id) === String(savedId) && String(destination.id) === String(currentId))
));

export const decoratorDestinationError = ({ destination, addressId, locked }) => {
  if (!destination?.rule) return '';
  const configured = hasCompleteDecoratorReceivingAddress(destination);
  // No address configured at all is allowed. But do not silently bypass a
  // previously selected company/saved source that is now unavailable.
  const hasSavedAddressSelection = /^[1-9][0-9]*$/.test(String(addressId ?? ''));
  if ((destination.rule.companyAddressId || destination.receivingAddressConfigured) &&
      !configured && !hasSavedAddressSelection) {
    return 'The saved decorator receiving address is unavailable. Ask an administrator to review it in Decorator Matrix.';
  }
  if (isDecoratorRuleAddress(addressId, destination.id) && !configured) {
    return 'The decorator receiving address changed. Reopen Shipping before saving.';
  }
  if (locked && configured && !addressId) {
    return 'Use the configured decorator receiving address before saving.';
  }
  return '';
};
// The onward Ship To is separate from the decorator's receiving location.
// Customer Pickup is a method, not a destination, so it does not waive this check.
export const decoratorOnwardRouteError = ({ shipToType, onward, companyAddress } = {}) => {
  if (String(shipToType || '').trim().toLowerCase() !== 'decorator') return '';
  const destination = String(onward || '').trim().toLowerCase();
  if (destination !== 'client' && destination !== 'elite') {
    return 'Choose where the decorator will send the finished goods (Client or Company).';
  }
  if (destination === 'elite' && !String(companyAddress ?? '').trim()) {
    return 'Select the company receiving address for the decorator onward shipment.';
  }
  return '';
};
// The Shipping page can supply either an Order object or the API response
// envelope { order: ... }; the URL route always carries the public order ID.
// Never send a local Order PK in place of the public OrderId.
export const resolveShippingPublicOrderId = ({ shippingRecord, order, routeOrderId } = {}) => (
  [
    shippingRecord?.OrderId,
    shippingRecord?.orderId,
    order?.OrderId,
    order?.order?.OrderId,
    order?.data?.order?.OrderId,
    routeOrderId,
  ].find((value) => value != null && String(value).trim()) || null
);
